import { driver, type Driver, type DriveStep } from "driver.js";
import "driver.js/dist/driver.css";
import "./v5-tour.css";

import type { V5TourDefinition, V5TourLive, V5TourRoutePatch, V5TourStep } from "./v5TourTypes";

/**
 * Driver.js wrapper for the V5 demo tours: guided (challenges gate Next) and
 * kiosk (auto-advance + loop, closing disabled, no persistence — safe on an
 * unattended booth machine).
 *
 * Route side-effects go through the workbench's own updateRoute seam; late-
 * rendering targets are tolerated by polling + driver.refresh() (the same
 * strategy the legacy OnboardingProvider used).
 */

export interface V5TourHandle {
  destroy: () => void;
}

export interface StartV5TourOptions {
  tour: V5TourDefinition;
  kiosk: boolean;
  applyRoute: (patch: V5TourRoutePatch) => void;
  live: { current: V5TourLive };
  /** Called when the tour is closed or completes (guided mode). */
  onEnd?: (reason: "complete" | "closed") => void;
  defaultDwellMs?: number;
}

const POLL_MS = 250;
const ELEMENT_WAIT_MS = 15000;

export function startV5Tour(options: StartV5TourOptions): V5TourHandle {
  const { tour, kiosk, applyRoute, live, onEnd } = options;
  const defaultDwell = options.defaultDwellMs ?? 9000;

  let disposed = false;
  let stepTimers: number[] = [];
  let stepIntervals: number[] = [];

  const clearStepWork = () => {
    for (const t of stepTimers) window.clearTimeout(t);
    for (const i of stepIntervals) window.clearInterval(i);
    stepTimers = [];
    stepIntervals = [];
  };

  const challengeDone: boolean[] = tour.steps.map((s) => !s.challenge);

  const setNextEnabled = (enabled: boolean) => {
    const btn = document.querySelector<HTMLButtonElement>(".driver-popover-next-btn");
    if (btn) {
      btn.disabled = !enabled;
      btn.classList.toggle("v5-tour-next-locked", !enabled);
    }
  };

  const afterHighlight = (index: number, step: V5TourStep, drv: Driver) => {
    clearStepWork();

    // 1. tolerate late-rendering targets. If the anchor wasn't in the DOM when
    // the step was highlighted, driver rendered a CENTERED popover with no
    // click-through cutout — refresh() can't recover that, so RE-DRIVE the
    // same step once the anchor appears (restores the highlight + the hole
    // that lets the user actually interact with the widget under tour).
    if (step.element) {
      const started = Date.now();
      const hadElementAtHighlight =
        !!document.querySelector(step.element) &&
        (document.querySelector(step.element) as HTMLElement).getClientRects().length > 0;
      const waiter = window.setInterval(() => {
        const el = document.querySelector(step.element as string);
        const visible = !!el && (el as HTMLElement).getClientRects().length > 0;
        if (visible) {
          window.clearInterval(waiter);
          if (hadElementAtHighlight) drv.refresh();
          else drv.moveTo(index); // re-highlight this step against the real anchor
        } else if (Date.now() - started > ELEMENT_WAIT_MS) {
          window.clearInterval(waiter);
        }
      }, POLL_MS);
      stepIntervals.push(waiter);
    }

    // 2. guided challenges: poll the live ref, gate Next
    if (!kiosk && step.challenge) {
      setNextEnabled(challengeDone[index] === true);
      const poller = window.setInterval(() => {
        if (challengeDone[index]) {
          window.clearInterval(poller);
          return;
        }
        if (step.challenge!.validate(live.current)) {
          challengeDone[index] = true;
          setNextEnabled(true);
          const pop = document.querySelector(".driver-popover");
          pop?.classList.add("v5-tour-challenge-done");
        }
      }, POLL_MS);
      stepIntervals.push(poller);
    }

    // 3. kiosk: dwell then advance; loop from the last step
    if (kiosk) {
      const dwell = step.dwellMs ?? defaultDwell;
      const timer = window.setTimeout(() => {
        if (disposed) return;
        if (drv.isLastStep()) drv.drive(0);
        else drv.moveNext();
      }, dwell);
      stepTimers.push(timer);
    }
  };

  const driveSteps: DriveStep[] = tour.steps.map((step, index) => ({
    element: step.element,
    popover: {
      title: step.popover.title,
      description:
        step.popover.description +
        (step.challenge && !kiosk
          ? `<div class="v5-tour-challenge">${step.challenge.instruction}</div>`
          : ""),
      side: step.popover.side,
      align: step.popover.align,
    },
    onHighlightStarted: () => {
      if (step.route) applyRoute(step.route);
    },
    onHighlighted: () => afterHighlight(index, step, drv),
  }));

  // --- button handling we OWN, at document capture, registered BEFORE the
  // driver instance exists so it precedes driver.js's own delegated handlers.
  // driver.js preventDefault()s pointerdown at document capture and relies on
  // the browser still synthesizing a `click` afterwards — recent Chrome
  // versions suppress that click, leaving the Next button dead. We act on
  // POINTERUP (never suppressed) with a `click` fallback for keyboard
  // activation, debounced so real clicks (pointerup+click) advance once, and
  // swallow the chain so driver's handler can't double-fire.
  // one physical activation emits pointerup THEN click — act on the first,
  // swallow its paired click (keyboard activation emits click alone and acts)
  let swallowNextClick = false;
  const act = (kind: "next" | "prev" | "close", ev: Event) => {
    ev.preventDefault();
    ev.stopPropagation();
    ev.stopImmediatePropagation();
    if (ev.type === "click" && swallowNextClick) {
      swallowNextClick = false;
      return;
    }
    swallowNextClick = ev.type === "pointerup";
    if (kind === "close") {
      drv.destroy();
      return;
    }
    if (kind === "prev") {
      drv.movePrevious();
      return;
    }
    const index = drv.getActiveIndex() ?? 0;
    const step = tour.steps[index];
    if (!kiosk && step?.challenge && !challengeDone[index]) return;
    if (drv.isLastStep()) drv.destroy();
    else drv.moveNext();
  };
  const onButtonEvent = (ev: Event) => {
    if (disposed) return;
    const t = ev.target as Element | null;
    if (!t || typeof t.closest !== "function") return;
    if (t.closest(".driver-popover-next-btn")) act("next", ev);
    else if (t.closest(".driver-popover-prev-btn")) act("prev", ev);
    else if (t.closest(".driver-popover-close-btn")) act("close", ev);
  };
  document.addEventListener("pointerup", onButtonEvent, true);
  document.addEventListener("click", onButtonEvent, true);
  const removeButtonHandlers = () => {
    document.removeEventListener("pointerup", onButtonEvent, true);
    document.removeEventListener("click", onButtonEvent, true);
  };

  const drv = driver({
    steps: driveSteps,
    showProgress: !kiosk,
    allowClose: !kiosk,
    allowKeyboardControl: !kiosk,
    // Overlay clicks are inert in BOTH modes: the tour narrates a live app and
    // invites interaction, so a stray click outside the cutout must not nuke
    // the whole tour (guided exits via the ✕ button).
    overlayClickBehavior: (() => {}) as never,
    showButtons: kiosk ? [] : ["next", "previous", "close"],
    stagePadding: 8,
    stageRadius: 10,
    popoverClass: kiosk ? "v5-tour-popover v5-tour-kiosk" : "v5-tour-popover",
    onDestroyed: () => {
      clearStepWork();
      removeButtonHandlers();
      if (!disposed) {
        disposed = true;
        onEnd?.(drv.isLastStep() ? "complete" : "closed");
      }
    },
  });

  // first step's route applies before the overlay exists
  const first = tour.steps[0];
  if (first?.route) applyRoute(first.route);
  drv.drive(0);

  return {
    destroy: () => {
      if (disposed) return;
      disposed = true;
      clearStepWork();
      removeButtonHandlers();
      drv.destroy();
    },
  };
}
